import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Button from "../components/inputs/Button";
import Dropdown from "../components/inputs/Dropdown";
import Input from "../components/inputs/Input";
import Badge from "../components/shared/Badge";
import Card from "../components/shared/Card";
import EmptyState from "../components/shared/EmptyState";
import { panelAPI } from "../services/api";

const PublicPanels = () => {
  const [panels, setPanels] = useState([]);
  const [filterStatus, setFilterStatus] = useState("ALL");
  const [searchTerm, setSearchTerm] = useState("");

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const isAuthenticated = !!localStorage.getItem("accessToken");

  useEffect(() => {
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }

    const fetchPublicPanels = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await panelAPI.getAllPanels();
        if (response.success) {
          setPanels(response.panels || []);
        } else {
          setError(response.message || "Failed to load public panels");
        }
      } catch (err) {
        console.error("Failed to fetch public panels:", err);
        setError("Failed to load public panels");
      } finally {
        setLoading(false);
      }
    };
    fetchPublicPanels();
  }, [isAuthenticated]);

  const filteredPanels = panels.filter((panel) => {
    const title = panel.title || "";
    const matchesSearch = title
      .toLowerCase()
      .includes(searchTerm.toLowerCase());
    const matchesFilter =
      filterStatus === "ALL" || panel.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  const getDecisionBadge = (decision) => {
    const decisionConfig = {
      APPROVED: { variant: "success", icon: "✓" },
      CERTIFIED: { variant: "primary", icon: "✓" },
      PARTIALLY_APPROVED: { variant: "warning", icon: "⚠" },
      DENIED: { variant: "danger", icon: "✗" },
    };

    const config = decisionConfig[decision] || decisionConfig["APPROVED"];
    return (
      <Badge variant={config.variant}>
        <span className="mr-1">{config.icon}</span>
        {decision.replace("_", " ")}
      </Badge>
    );
  };

  const getStatistics = () => {
    return {
      total: panels.length,
      approved: panels.filter((p) => p.decisions?.[0]?.decision === "APPROVED")
        .length,
      certified: panels.filter(
        (p) => p.decisions?.[0]?.decision === "CERTIFIED"
      ).length,
    };
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white shadow-lg rounded-lg p-8 max-w-md w-full text-center">
          <div className="mb-4">
            <svg
              className="w-16 h-16 text-custom-blue mx-auto"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
              />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Authentication Required
          </h2>
          <p className="text-gray-600 mb-6">
            You must login to view panels.
          </p>
          <Button
            variant="primary"
            onClick={() => navigate("/login")}
            className="w-full"
          >
            Go to Login
          </Button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-lg text-gray-600">Loading public panels...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      </div>
    );
  }

  const stats = getStatistics();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header Section */}
      <div className="bg-gradient-to-br from-custom-blue to-custom-light-blue text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl font-bold mb-4">Public Panel Decisions</h1>
          <p className="text-xl opacity-90">
            Browse completed Scientific Resolution Panel reviews and decisions
          </p>
        </div>
      </div>

      {/* Search and Filter Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Card variant="elevated" className="mb-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Search Panels"
              type="text"
              name="search"
              value={searchTerm}
              field={{
                onChange: (e) => setSearchTerm(e.target.value),
                value: searchTerm,
              }}
              placeholder="Search by title..."
            />
            <Dropdown
              headLabel="Filter by Status"
              label="All Published Panels"
              options={[
                { value: "ALL", label: "All Published Panels" },
                { value: "PUBLISHED", label: "Recently Published" },
                { value: "COMPLETED", label: "Completed" },
              ]}
              value={filterStatus}
              onSelect={setFilterStatus}
            />
          </div>
        </Card>

        {/* Statistics */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <Card className="bg-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Published</p>
                <p className="text-2xl font-bold text-custom-blue">
                  {stats.total}
                </p>
              </div>
              <div className="bg-blue-100 p-2 rounded-lg">
                <svg
                  className="w-6 h-6 text-custom-blue"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
              </div>
            </div>
          </Card>

          <Card className="bg-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Approved</p>
                <p className="text-2xl font-bold text-green-600">
                  {stats.approved}
                </p>
              </div>
              <div className="bg-green-100 p-2 rounded-lg">
                <svg
                  className="w-6 h-6 text-green-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
            </div>
          </Card>

          <Card className="bg-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Certified</p>
                <p className="text-2xl font-bold text-blue-600">
                  {stats.certified}
                </p>
              </div>
              <div className="bg-blue-100 p-2 rounded-lg">
                <svg
                  className="w-6 h-6 text-blue-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z"
                  />
                </svg>
              </div>
            </div>
          </Card>

          {/* Communities statistic removed */}
        </div>

        {/* Panels Grid */}
        {filteredPanels.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredPanels.map((panel) => (
              <Card
                key={panel.id}
                variant="elevated"
                className="hover:shadow-xl transition-shadow duration-300 cursor-pointer"
              >
                <div className="flex justify-between items-start mb-3">
                  <h3 className="text-lg font-bold text-gray-900 flex-1">
                    {panel.panelName}
                  </h3>
                  {panel.decisions?.[0] &&
                    getDecisionBadge(panel.decisions[0].decision)}
                </div>

                <p className="text-sm text-gray-600 mb-2">{panel.panelType}</p>
                {/* Description removed */}

                {panel.decisionSummary && (
                  <div className="bg-gray-50 rounded-lg p-3 mb-4">
                    <p className="text-xs font-semibold text-gray-700 mb-1">
                      Decision Summary:
                    </p>
                    <p className="text-xs text-gray-600">
                      {panel.decisionSummary}
                    </p>
                  </div>
                )}
                {panel.requestSummary && (
                  <div className="bg-gray-50 rounded-lg p-3 mb-4">
                    <p className="text-xs font-semibold text-gray-700 mb-1">
                      Request Summary:
                    </p>
                    <p className="text-xs text-gray-600">
                      {panel.requestSummary}
                    </p>
                  </div>
                )}
                <div className="flex justify-between items-center">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate(`/panel/${panel.id}`)}
                  >
                    View Details →
                  </Button>
                  {panel.finalReport && (
                    <Button
                      variant="primary"
                      size="sm"
                      className="bg-emerald-800"
                    >
                      Download Report
                    </Button>
                  )}
                </div>
              </Card>
            ))}
          </div>
        ) : (
          <EmptyState
            icon={
              <svg
                className="w-16 h-16 text-gray-400 mx-auto mb-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                />
              </svg>
            }
            title="No Panels Found"
            message="Try adjusting your search or filter criteria."
          />
        )}
      </div>
    </div>
  );
};

export default PublicPanels;
